import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { faAngleRight } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { FeedPostModalMainPlaylistDocument, useQuery } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { FeedPostModalMainPlaylistHeader } from './Header';
import { PreviewReleaseRow } from './ReleaseRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  query FeedPostModalMainPlaylist($playlistId: UUID!) {
    shelf(id: $playlistId) {
      id
      releaseCount
      webappUri
      user {
        id
        webappUri
        username
      }
      orderedReleases(pagination: { first: 3 }) {
        edges {
          node {
            id
            release {
              id
              ...FeedPostModalPlaylistPreview
            }
          }
        }
      }

      ...FeedPostModalMainPlaylistHeader
    }
  }
`);

export const FeedPostModalMainPlaylist = ({
  playlistId,
  onEditPlaylistClicked,
  allowEdit,
}: {
  playlistId: string;
  onEditPlaylistClicked: () => void;
  allowEdit: boolean;
}) => {
  const { data, isLoading } = useQuery(FeedPostModalMainPlaylistDocument, {
    variables: {
      playlistId,
    },
    staleTime: 0,
  });

  const playlist = data?.data.shelf;

  if (!playlist || isLoading)
    return <div className="h-64 w-full animate-pulse rounded-lg bg-neutral200 shadow-tertiary" />;

  const showViewMore = playlist.releaseCount > 3;

  const releases = playlist.orderedReleases.edges.map(edge => edge.node.release);
  const hasReleases = playlist.releaseCount > 0 && releases.length > 0;

  return (
    <div className="rounded-lg shadow-tertiary">
      <FeedPostModalMainPlaylistHeader
        playlistFrag={playlist}
        onEditPlaylistClicked={onEditPlaylistClicked}
        allowEdit={!!playlistId && hasReleases && allowEdit}
      />
      {hasReleases ? (
        releases.map(release => (
          <PreviewReleaseRow key={release.id} relaseFrag={release} playlistId={playlistId} />
        ))
      ) : (
        <p className="flex h-14 items-center justify-center text-base-m text-base600">
          There are no sounds in here.
        </p>
      )}
      {showViewMore && (
        <div className="h-[38px] rounded-b-lg border-t border-base100 bg-white text-base-m text-base500 hover:cursor-pointer hover:text-base800">
          <Link
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-full w-full items-center justify-center gap-[6px] font-medium"
            href={webappUriPath(playlist.webappUri)}
          >
            View all {playlist.releaseCount} sound{playlist.releaseCount > 1 && 's'}
            <FontAwesomeIcon icon={faAngleRight} />
          </Link>
        </div>
      )}
    </div>
  );
};
